import React, { useEffect, useState, useCallback } from 'react';
import { adminApi } from '../../services/api';
import toast from 'react-hot-toast';
import { Upload, Trash2, FileText, Download, Plus, X, Edit } from 'lucide-react';

export default function AdminOnboarding() {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [uploading, setUploading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminApi.getOnboardingDocs();
      setDocs(res.data.results || res.data);
    } catch { toast.error('Error al cargar documentos.'); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleUpload = async () => {
    if (!modal?.name || !modal?.file) {
      toast.error('Nombre y archivo son obligatorios.');
      return;
    }
    setUploading(true);
    try {
      await adminApi.uploadOnboardingDoc({
        name: modal.name,
        description: modal.description || '',
        doc_type: modal.doc_type || 'download',
        file: modal.file,
        order: modal.order || 0,
        is_active: true,
      });
      toast.success('Documento subido.');
      setModal(null);
      load();
    } catch { toast.error('Error al subir.'); }
    setUploading(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este documento?')) return;
    try {
      await adminApi.deleteOnboardingDoc(id);
      toast.success('Documento eliminado.');
      load();
    } catch { toast.error('Error al eliminar.'); }
  };

  const handleToggleActive = async (doc) => {
    try {
      await adminApi.updateOnboardingDoc(doc.id, { is_active: !doc.is_active });
      toast.success(doc.is_active ? 'Desactivado' : 'Activado');
      load();
    } catch { toast.error('Error al actualizar.'); }
  };

  // La API devuelve URLs absolutas; no se antepone ningún prefijo.

  const TYPE_LABELS = {
    'download': 'Solo descarga',
    'fill_upload': 'Descargar, rellenar y subir',
  };

  return (
    <div>
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h1>Documentos de Onboarding</h1>
          <p>Documentos que los nuevos empleados descargan, rellenan y/o suben</p>
        </div>
        <button className="btn btn--primary" onClick={() => setModal({ name: '', description: '', doc_type: 'download', file: null, order: 0 })}>
          <Plus size={16} /> Subir documento
        </button>
      </div>

      <div className="alert alert--info" style={{ fontSize: '0.84rem', marginBottom: 20 }}>
        <strong>Tipos de documento:</strong>
        <br />— <strong>Solo descarga:</strong> documentos informativos que el empleado solo lee (protocolos, trípticos PRL).
        <br />— <strong>Descargar, rellenar y subir:</strong> documentos que el empleado descarga, rellena/firma y vuelve a subir (diplomas, exámenes, registros EPI).
      </div>

      {loading ? <div className="spinner" /> : (
        <div className="card">
          {docs.length === 0 ? (
            <div className="empty-state"><p>No hay documentos de onboarding cargados</p></div>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>Orden</th>
                  <th>Nombre</th>
                  <th>Tipo</th>
                  <th>Activo</th>
                  <th>Fecha</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {docs.map(d => (
                  <tr key={d.id} style={{ opacity: d.is_active ? 1 : 0.5 }}>
                    <td>{d.order}</td>
                    <td style={{ fontWeight: 500 }}>
                      <FileText size={14} style={{ verticalAlign: 'middle', marginRight: 6, color: d.doc_type === 'fill_upload' ? 'var(--amber-500)' : 'var(--gray-400)' }} />
                      {d.name}
                      {d.description && <div style={{ fontSize: '0.78rem', color: 'var(--gray-500)', marginTop: 2 }}>{d.description}</div>}
                    </td>
                    <td>
                      <span className={`badge ${d.doc_type === 'fill_upload' ? 'badge--warning' : 'badge--info'}`}>
                        {TYPE_LABELS[d.doc_type] || d.doc_type}
                      </span>
                    </td>
                    <td>
                      <button
                        className={`btn btn--sm ${d.is_active ? 'btn--primary' : 'btn--ghost'}`}
                        onClick={() => handleToggleActive(d)}
                        style={{ fontSize: '0.75rem', padding: '4px 10px' }}
                      >
                        {d.is_active ? 'Activo' : 'Inactivo'}
                      </button>
                    </td>
                    <td style={{ fontSize: '0.82rem' }}>{new Date(d.uploaded_at).toLocaleDateString('es-ES')}</td>
                    <td>
                      <div style={{ display: 'flex', gap: 4 }}>
                        <a href={d.file} target="_blank" rel="noreferrer" className="btn btn--ghost btn--sm">
                          <Download size={14} />
                        </a>
                        <button className="btn btn--ghost btn--sm" onClick={() => handleDelete(d.id)}>
                          <Trash2 size={14} color="var(--danger)" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Upload Modal */}
      {modal && (
        <div className="modal-overlay" onClick={() => setModal(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal__header">
              <h3>Subir documento de onboarding</h3>
              <button className="btn btn--ghost btn--sm" onClick={() => setModal(null)}><X size={18} /></button>
            </div>
            <div className="form-group">
              <label>Nombre *</label>
              <input className="form-input" value={modal.name} onChange={e => setModal({ ...modal, name: e.target.value })} placeholder="Ej: Diploma editable" />
            </div>
            <div className="form-group">
              <label>Descripción</label>
              <input className="form-input" value={modal.description} onChange={e => setModal({ ...modal, description: e.target.value })} placeholder="Breve descripción del documento" />
            </div>
            <div className="form-group">
              <label>Tipo de documento *</label>
              <select className="form-input" value={modal.doc_type} onChange={e => setModal({ ...modal, doc_type: e.target.value })}>
                <option value="download">Solo descarga (informativo)</option>
                <option value="fill_upload">Descargar, rellenar/firmar y subir</option>
              </select>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Archivo *</label>
                <input type="file" className="form-input" accept=".pdf,.doc,.docx,.jpg,.png" onChange={e => setModal({ ...modal, file: e.target.files[0] })} style={{ padding: 8 }} />
              </div>
              <div className="form-group">
                <label>Orden</label>
                <input className="form-input" type="number" value={modal.order} onChange={e => setModal({ ...modal, order: parseInt(e.target.value) || 0 })} />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
              <button className="btn btn--secondary" onClick={() => setModal(null)}>Cancelar</button>
              <button className="btn btn--primary" onClick={handleUpload} disabled={uploading}>
                <Upload size={16} /> {uploading ? 'Subiendo...' : 'Subir documento'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
